import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  RefreshCw, 
  AlertCircle, 
  GraduationCap,
  Layers,
  LayoutGrid
} from 'lucide-react';
import AcademicContextSelector from './AcademicContextSelector';
import SubjectCard from './SubjectCard';
import api from '../api';

const COEMarksDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const normalizeContext = useCallback((ctx) => {
    if (!ctx) return null;
    return {
      ...ctx,
      semesterId: ctx.semesterId || ctx.semId || null,
      semesterName: ctx.semesterName || ctx.semName || '',
      semId: ctx.semId || ctx.semesterId || null,
      semName: ctx.semName || ctx.semesterName || ''
    };
  }, []);

  const [context, setContext] = useState(normalizeContext(location.state?.context || null));
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isSameContext = useCallback((a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;
    return (
      (a.programTypeId || null) === (b.programTypeId || null) &&
      (a.programId || null) === (b.programId || null) &&
      (a.academicYearId || null) === (b.academicYearId || null) &&
      (a.semesterId || a.semId || null) === (b.semesterId || b.semId || null) &&
      (a.termId || null) === (b.termId || null) &&
      (a.evaluationMode || null) === (b.evaluationMode || null)
    );
  }, []);

  const handleContextChange = useCallback((ctx) => {
    const normalized = normalizeContext(ctx);
    setContext((prev) => (isSameContext(prev, normalized) ? prev : normalized));
  }, [normalizeContext, isSameContext]);

  // Fetch subject summaries based on context
  const fetchSubjectSummaries = useCallback(async () => {
    if (!context || !context.programTypeId || !context.programId || !context.academicYearId) {
      return;
    }
    // Check if semester or term is selected
    const semesterId = context.semesterId || context.semId;
    const termId = context.termId;

    if (!semesterId && !termId) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams();
      params.append('programTypeId', context.programTypeId);
      params.append('programId', context.programId);
      params.append('academicYearId', context.academicYearId);
      
      if (semesterId) params.append('semesterId', semesterId);
      if (termId) params.append('termId', termId);
      
      const response = await api.get(`/api/coe/marks/subjects/summary?${params.toString()}`);
      
      if (response.data.success) {
        const subjectsData = response.data.data.subjects || [];
        setSubjects(subjectsData);
      }
    } catch (err) {
      console.error('Error fetching subjects:', err);
      setError(err.response?.data?.message || 'Failed to fetch subjects');
    } finally {
      setLoading(false);
    }
  }, [context]);

  useEffect(() => {
    fetchSubjectSummaries();
  }, [fetchSubjectSummaries]);

  const handleSubjectClick = (subject) => {
    navigate('/coe/subject-marks', {
      state: {
        context,
        subject: {
          subjectId: subject.subjectId,
          subjectName: subject.subjectName,
          subjectCode: subject.subjectCode,
          isLab: subject.isLab,
          facultyId: subject.facultyId,
          facultyName: subject.facultyName || 'Not Assigned'
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 antialiased selection:bg-indigo-100 pb-12">
      {/* Dashboard Top Header Control */}
      <header className="bg-white border-b border-slate-200/80 px-4 py-5 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="bg-slate-950 text-white font-mono text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md">
                COE Terminal
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 mt-1 tracking-tight">
              Marks Overview Dashboard
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5">
              Select an active academic parameter context to populate performance ledgers.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        {/* Isolated Context Selector Container */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-2 sm:p-4">
          <AcademicContextSelector 
            tenantId={localStorage.getItem('tenantId')}
            onContextChange={handleContextChange}
            initialContext={context}
          />
        </div>

        {/* Dynamic Catalog Section */}
        <section className="mt-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 bg-white border border-slate-200 rounded-2xl shadow-sm">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Compiling Curricular Summary Arrays...
              </p>
            </div>
          ) : error ? (
            <div className="bg-rose-50/50 border border-rose-200 rounded-2xl p-8 text-center max-w-lg mx-auto shadow-sm">
              <div className="w-12 h-12 bg-rose-50 border border-rose-100 text-rose-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-5 h-5" />
              </div>
              <h4 className="text-slate-900 font-bold text-base">Dashboard Request Exception</h4>
              <p className="text-sm text-rose-700 font-medium mt-1">{error}</p>
              <button 
                onClick={fetchSubjectSummaries} 
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 font-semibold text-xs rounded-xl transition-colors shadow-sm"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Re-attempt Connection
              </button>
            </div>
          ) : !context?.programTypeId ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center shadow-sm max-w-xl mx-auto">
              <div className="w-12 h-12 bg-slate-50 border border-slate-100 text-slate-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-6 h-6" />
              </div>
              <p className="text-sm font-bold text-slate-800">Awaiting Curricular Scope</p>
              <p className="text-xs text-slate-400 font-medium mt-1">
                Please complete the master program configurations inside the selector above to load metrics.
              </p>
            </div>
          ) : !(context?.semesterId || context?.semId || context?.termId) ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center shadow-sm max-w-xl mx-auto">
              <div className="w-12 h-12 bg-indigo-50 border border-indigo-100 text-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Layers className="w-5 h-5" />
              </div>
              <p className="text-sm font-bold text-slate-800">Timeline Parameter Missing</p>
              <p className="text-xs text-slate-400 font-medium mt-1">
                Isolate your query down to an explicit term or semester block inside the active filters.
              </p>
            </div>
          ) : subjects.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center shadow-sm max-w-xl mx-auto">
              <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <LayoutGrid className="w-5 h-5" />
              </div>
              <p className="text-sm font-bold text-slate-800">Zero Course Profiles Resolved</p>
              <p className="text-xs text-slate-400 font-medium mt-1">
                No active operational subject configurations match the parameters provided in this sector.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between px-1">
                <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Resolved Course Ledgers ({subjects.length})
                </h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
                {subjects.map((subject) => (
                  <SubjectCard 
                    key={subject.subjectId} 
                    subject={subject} 
                    onClick={() => handleSubjectClick(subject)}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default COEMarksDashboard;